import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuthStore } from "../state/authStore";
import { useTeamStore } from "../state/teamStore";
import { useDesktopAgentStatus } from "../hooks/useDesktopAgentStatus";
import CreateSeriesModal from "../components/CreateSeriesModal";
import { api } from "../utils/api";
import { formatSeriesDisplayName } from "../utils/formatters";
import { slugifySeriesName } from "../utils/slug";
import type { Team, TeamHomeSummary, TeamSeriesSummary } from "../types";

export default function TeamHome() {
  const { teamId } = useParams<{ teamId: string }>();
  const { user } = useAuthStore();
  const { activeTeamId, setActiveTeam, members, fetchMembers } = useTeamStore();
  const { status: desktopStatus } = useDesktopAgentStatus();

  const [team, setTeam] = useState<Team | null>(null);
  const [memberCount, setMemberCount] = useState(0);
  const [seriesSummaries, setSeriesSummaries] = useState<TeamSeriesSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => {
    if (teamId && teamId !== activeTeamId) {
      setActiveTeam(teamId);
    }
  }, [teamId, activeTeamId, setActiveTeam]);

  useEffect(() => {
    if (!teamId) return;
    let cancelled = false;

    setLoading(true);
    setError(null);

    api<TeamHomeSummary>(`/api/teams/${teamId}/home`)
      .then((data) => {
        if (cancelled) return;
        setTeam(data.team);
        setMemberCount(data.memberCount || 0);
        setSeriesSummaries(data.seriesSummaries || []);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || "Failed to load team");
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [teamId, reloadNonce]);

  useEffect(() => {
    if (teamId) {
      void fetchMembers(teamId);
    }
  }, [teamId, fetchMembers]);

  if (loading) {
    return (
      <div className="page">
        <div className="empty-state">
          <div className="spinner spinner-lg" />
          <div className="empty-state-title">Loading team workspace</div>
          <div className="empty-state-text">Pulling series cards, recent uploads, and team activity.</div>
        </div>
      </div>
    );
  }

  if (error || !team) {
    return (
      <div className="page">
        <div className="empty-state">
          <div className="empty-state-title">Team not available</div>
          <div className="empty-state-text">{error || "This team could not be loaded."}</div>
        </div>
      </div>
    );
  }

  const isAdminOrOwner =
    team.myRole === "owner" ||
    team.myRole === "admin" ||
    members.some((member) => member.userId === user?.id && (member.role === "owner" || member.role === "admin"));
  // Custom series live in seriesSummaries (from the CustomSeries table), NOT in
  // enabledSeries — so a custom-only team must still show its grid (#282).
  const hasConfiguredSeries = (team.enabledSeries?.length || 0) > 0 || seriesSummaries.length > 0;

  return (
    <div className="page page--wide">
      <header className="editorial-header">
        <div className="editorial-meta">
          <div className="editorial-meta-left" style={{ display: "flex", alignItems: "center", gap: "0.7rem" }}>
            {team.logoUrl ? (
              <img src={team.logoUrl} alt={`${team.name} logo`} style={styles.headerLogo} />
            ) : (
              <div style={styles.headerLogoFallback}>{team.name.charAt(0).toUpperCase()}</div>
            )}
            <span className="eyebrow">
              {[
                `${memberCount} members`,
                // seriesSummaries covers synced + custom; enabledSeries misses
                // custom-only teams (they'd read "0 series" over a full grid, #282).
                `${seriesSummaries.length} series`,
                team.myRole || null,
              ].filter(Boolean).join("  ·  ")}
            </span>
          </div>
          <div className="editorial-meta-right" style={styles.headerPills}>
            {desktopStatus?.status === "connected" && <span className="pill pill--success">Agent connected</span>}
            {teamId && (
              <Link to={`/teams/${teamId}/setups`} className="btn btn-secondary">
                Setup library
              </Link>
            )}
            {teamId && (
              <Link to={`/teams/${teamId}/checklists`} className="btn btn-secondary">
                Checklists
              </Link>
            )}
            {isAdminOrOwner && teamId && (
              <button type="button" className="btn btn-secondary" onClick={() => setShowCustomModal(true)}>
                New custom series
              </button>
            )}
            {isAdminOrOwner && teamId && (
              <Link to={`/teams/${teamId}/settings`} className="btn btn-secondary">
                Team settings
              </Link>
            )}
          </div>
        </div>
        <h1 className="editorial-title">{team.name}</h1>
      </header>

      {showCustomModal && teamId && (
        <CreateSeriesModal
          teamId={teamId}
          onClose={() => setShowCustomModal(false)}
          onCreated={() => {
            setShowCustomModal(false);
            setReloadNonce((n) => n + 1);
          }}
        />
      )}

      {desktopStatus?.status !== "connected" && (
        <section className="section-shell" style={{ marginTop: "1rem" }}>
          <div className="section-header">
            <div>
              <div className="section-kicker">Desktop Agent</div>
              <div className="section-heading">
                {desktopStatus?.status === "offline"
                  ? "Your Windows agent is installed but not online."
                  : desktopStatus?.status === "needs_attention"
                  ? "Your Windows agent needs attention."
                  : "Install the Windows agent before your next session."}
              </div>
            </div>
          </div>
          <div
            className="card"
            style={{
              ...styles.agentBanner,
              ...(desktopStatus?.status === "offline" || desktopStatus?.status === "needs_attention"
                ? { borderLeft: "3px solid var(--flag-yellow)" }
                : {}),
            }}
          >
            <div style={styles.agentBannerCopy}>
              {desktopStatus?.status === "offline"
                ? "Your agent was set up before but hasn't checked in recently. Open it on your iRacing PC and turn it back on."
                : desktopStatus?.status === "needs_attention"
                ? "The agent is installed, but watcher or telemetry capture is not fully running. Open the Windows app and get it back to a healthy running state."
                : "Install the Windows desktop agent on the PC that runs iRacing so uploads happen automatically instead of relying on manual fallback uploads."}
            </div>
            <div style={styles.agentBannerActions}>
              <Link to="/desktop" className="btn btn-primary">
                Open desktop agent guide
              </Link>
              <Link to="/settings" className="btn btn-secondary">
                Manual upload fallback
              </Link>
            </div>
          </div>
        </section>
      )}

      {!hasConfiguredSeries ? (
        <div className="empty-state" style={{ minHeight: "20rem" }}>
          <div className="empty-state-title">No series selected yet</div>
          <div className="empty-state-text">
            {isAdminOrOwner
              ? "Turn on the series your team actually runs so the team page only shows the cards you care about."
              : "This team has not selected any series to monitor yet."}
          </div>
          {isAdminOrOwner && teamId && (
            <Link to={`/teams/${teamId}/settings`} className="btn btn-primary">
              Choose Team Series
            </Link>
          )}
        </div>
      ) : (
        <section className="section-shell">
          <div className="section-header">
            <div>
              <div className="section-kicker">Active Series</div>
              <div className="section-heading">What the team is running this season.</div>
            </div>
          </div>

          {seriesSummaries.length === 0 ? (
            <div className="empty-state" style={{ minHeight: "16rem" }}>
              <div className="empty-state-title">No series data yet</div>
              <div className="empty-state-text">
                The team has enabled series cards, but no schedule or run data has been loaded for them yet.
              </div>
            </div>
          ) : (
            <div style={styles.seriesGrid}>
              {seriesSummaries.map((summary) => (
                <SeriesCard key={summary.seriesSlug} teamId={teamId!} summary={summary} />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function SeriesCard({ teamId, summary }: { teamId: string; summary: TeamSeriesSummary }) {
  const slug = summary.seriesSlug || slugifySeriesName(summary.seriesName);
  const currentWeek = summary.currentWeek;
  const upcomingWeek = summary.upcomingWeek;
  const activeWeek = currentWeek || upcomingWeek;
  const wn = activeWeek?.displayWeekNumber ?? activeWeek?.weekNumber;

  // Status reads as the focal eyebrow (semantic flag color), so the week number
  // lives here in one line — no separate mono "Wk N" pill or redundant Status row.
  const statusText = currentWeek
    ? wn != null
      ? `Live · Wk ${wn}`
      : "Live"
    : upcomingWeek
      ? wn != null
        ? `Upcoming · Wk ${wn}`
        : "Upcoming"
      : "Planned";

  return (
    <Link to={`/teams/${teamId}/series/${slug}`} style={{ textDecoration: "none" }}>
      <article className="card card-hover" style={styles.seriesCard}>
        <span className={`eyebrow num${currentWeek ? " eyebrow--live" : ""}`}>
          <span className={`flag-dot${currentWeek ? " flag-dot--live" : ""}`} aria-hidden="true" />
          {statusText}
        </span>

        {summary.seriesLogoUrl && (
          <img
            src={summary.seriesLogoUrl}
            alt=""
            style={styles.seriesLogo}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        )}

        <div>
          <h3 style={styles.seriesName}>{formatSeriesDisplayName(summary.seriesName)}</h3>
          {summary.carName && <div style={styles.seriesCar}>{summary.carName}</div>}
        </div>

        <div style={styles.trackLine} className="num" title={activeWeek?.trackName || undefined}>
          {activeWeek?.trackName || <span style={{ color: "var(--text-muted)" }}>Schedule pending</span>}
        </div>

        <div style={styles.metaFooter} className="num">
          {summary.runCount === 0 ? (
            <span style={{ color: "var(--text-muted)" }}>No runs yet</span>
          ) : (
            <>
              {summary.runCount} run{summary.runCount === 1 ? "" : "s"}
              <span style={{ color: "var(--text-muted)" }}> · </span>
              {summary.driverCount} driver{summary.driverCount === 1 ? "" : "s"}
            </>
          )}
        </div>
      </article>
    </Link>
  );
}

const styles: Record<string, React.CSSProperties> = {
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "1.25rem",
    paddingBottom: "1.5rem",
    borderBottom: "1px solid var(--rule-strong)",
    flexWrap: "wrap",
  },
  headerMain: {
    display: "flex",
    alignItems: "flex-start",
    gap: "1rem",
    minWidth: 0,
    flex: 1,
  },
  headerLogo: {
    width: "4.5rem",
    height: "4.5rem",
    borderRadius: "20px",
    objectFit: "cover",
    border: "1px solid var(--rule-strong)",
    flexShrink: 0,
  },
  headerLogoFallback: {
    width: "4.5rem",
    height: "4.5rem",
    borderRadius: "var(--r-lg)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "var(--live-soft)",
    border: "1px solid var(--live-edge)",
    color: "var(--live-strong)",
    fontFamily: "var(--font-display)",
    fontSize: "1.5rem",
    fontWeight: 700,
    flexShrink: 0,
  },
  headerCopy: {
    minWidth: 0,
  },
  headerTitle: {
    fontFamily: "var(--font-display)",
    fontSize: "clamp(1.8rem, 3vw, 2.5rem)",
    fontWeight: 800,
    color: "var(--text-strong)",
    lineHeight: 1.05,
    letterSpacing: "-0.04em",
    marginBottom: "0.75rem",
  },
  headerPills: {
    display: "flex",
    gap: "0.6rem",
    flexWrap: "wrap",
  },

  seriesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "1rem",
  },
  seriesCard: {
    minHeight: "100%",
    padding: "1.5rem",
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
  },
  seriesLogo: {
    // Heterogeneous trimmed logos: bound both dims so aspect is preserved (no
    // distortion) and every mark reads at a consistent height, left-aligned.
    maxHeight: 34,
    maxWidth: "65%",
    width: "auto",
    height: "auto",
    objectFit: "contain",
    objectPosition: "left center",
    display: "block",
  },
  seriesName: {
    fontFamily: "var(--font-display)",
    fontSize: "1.25rem",
    fontWeight: 700,
    color: "var(--text-strong)",
    lineHeight: 1.2,
  },
  seriesCar: {
    marginTop: "0.25rem",
    color: "var(--text-secondary)",
    fontSize: "0.8125rem",
    fontWeight: 500,
  },
  trackLine: {
    fontFamily: "var(--font-body)",
    fontSize: "0.95rem",
    fontWeight: 500,
    color: "var(--text-strong)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
  },
  metaFooter: {
    marginTop: "auto",
    borderTop: "1px solid var(--rule-faint)",
    paddingTop: "0.85rem",
    fontFamily: "var(--font-body)",
    fontSize: "0.8125rem",
    fontWeight: 500,
    color: "var(--text-secondary)",
  },
  agentBanner: {
    padding: "1.25rem",
    display: "flex",
    justifyContent: "space-between",
    gap: "1rem",
    alignItems: "center",
    flexWrap: "wrap",
  },
  agentBannerCopy: {
    flex: 1,
    minWidth: "16rem",
    color: "var(--text-secondary)",
    lineHeight: 1.65,
  },
  agentBannerActions: {
    display: "flex",
    gap: "0.75rem",
    flexWrap: "wrap",
  },
};
